import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

interface BudgetChartProps {
  budget: number;
  spent: number;
  available: number;
  budgetStatus: 'under' | 'over';
}

const BudgetChart: React.FC<BudgetChartProps> = ({
  budget,
  spent,
  available,
  budgetStatus,
}) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const isOverBudget = budgetStatus === 'over';

  // Data for bar chart
  const barData = [
    {
      name: 'Presupuesto',
      Presupuesto: budget,
      Gastado: spent,
    },
  ];

  // Data for pie chart
  const pieData = isOverBudget
    ? [
        { name: 'Gastado', value: budget, color: '#dc2626' },
        { name: 'Excedido', value: Math.abs(available), color: '#991b1b' },
      ]
    : [
        { name: 'Gastado', value: spent, color: '#3b82f6' },
        { name: 'Disponible', value: available, color: '#10b981' },
      ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="text-sm">
              <span className="font-medium">{entry.name}: </span>
              <span className="font-bold">{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <div className="text-sm">
            <span className="font-medium">{payload[0].name}: </span>
            <span className="font-bold">{formatCurrency(payload[0].value)}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Bar Chart - Budget vs Spent */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Comparación Presupuesto vs Gastado
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" stroke="#6b7280" />
            <YAxis
              stroke="#6b7280"
              tickFormatter={(value) =>
                new Intl.NumberFormat('es-CL', {
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(value)
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="Presupuesto" fill="#3b82f6" radius={[8, 8, 0, 0]} />
            <Bar
              dataKey="Gastado"
              fill={isOverBudget ? '#dc2626' : '#10b981'}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pie Chart - Budget Distribution */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          {isOverBudget
            ? 'Distribución del Gasto (Excedido)'
            : 'Distribución del Presupuesto'}
        </h3>
        <div className="flex flex-col lg:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <ResponsiveContainer width={250} height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1 space-y-3 w-full">
            {isOverBudget ? (
              <>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-red-600 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Presupuesto Original
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(budget)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-red-900 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Monto Excedido
                    </span>
                  </div>
                  <span className="text-sm font-bold text-red-600">
                    {formatCurrency(Math.abs(available))}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border-2 border-gray-300">
                  <span className="text-sm font-medium text-gray-700">
                    Total Gastado
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(spent)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-blue-600 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Gastado
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(spent)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-green-600 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Disponible
                    </span>
                  </div>
                  <span className="text-sm font-bold text-green-600">
                    {formatCurrency(available)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border-2 border-gray-300">
                  <span className="text-sm font-medium text-gray-700">
                    Presupuesto Total
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(budget)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status Alert */}
      <div
        className={`p-4 rounded-lg border-2 ${
          isOverBudget
            ? 'bg-red-50 border-red-200'
            : 'bg-green-50 border-green-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {isOverBudget ? (
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <h4
              className={`text-sm font-semibold mb-1 ${
                isOverBudget ? 'text-red-800' : 'text-green-800'
              }`}
            >
              {isOverBudget
                ? 'Presupuesto Excedido'
                : 'Presupuesto Bajo Control'}
            </h4>
            <p
              className={`text-sm ${
                isOverBudget ? 'text-red-700' : 'text-green-700'
              }`}
            >
              {isOverBudget
                ? `El proyecto ha excedido el presupuesto por ${formatCurrency(
                    Math.abs(available)
                  )}. Se puede continuar ingresando gastos.`
                : `El proyecto tiene ${formatCurrency(
                    available
                  )} disponibles del presupuesto total de ${formatCurrency(
                    budget
                  )}.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetChart;
